import { Modal } from '@maxtropy/components';
import DMYDateSwitch from '../components/DMYDateSwitch';
import * as echarts from 'echarts';
import { Dayjs } from 'dayjs';
import { FC, useEffect, useState, useRef } from 'react';
import styles from './index.module.scss';
import { apiV2WaterMapDataEnergyMediumUsagePost } from '@maxtropy/device-customer-apis-v2';
import { isNil } from 'lodash-es';

interface WaterWorksModalProps {
  groupId: string | number;
  unitId: string | number;
  energyMediumId: number;
  stationType: number;
  showModal: boolean;
  closeModal: () => void;
}

const WaterWorksModal: FC<WaterWorksModalProps> = ({
  showModal,
  closeModal,
  groupId,
  unitId,
  stationType,
  energyMediumId,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [rangeDate, setRangeDate] = useState<[Dayjs, Dayjs] | null>(null);
  const [activeKey, setActiveKey] = useState<'HOUR' | 'DAY' | 'WEEK' | 'MONTH' | 'YEAR'>('DAY');
  const [data, setData] = useState<{ time: string; value: number | undefined }[]>([]);
  const [avgValue, setAvgValue] = useState<number>(0);
  const seriesName = stationType === 3 ? '排放量' : '用水量';
  useEffect(() => {
    if (!rangeDate) return;
    const [start, end] = rangeDate;
    if (!start || !end) return;
    const startTime = start.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
    const endTime = end.format('YYYY-MM-DDTHH:mm:ss.SSSZ');

    apiV2WaterMapDataEnergyMediumUsagePost({
      groupId,
      unitId,
      type: activeKey,
      startTime,
      endTime,
      typeRes: [
        {
          energyMediumId,
        },
      ],
    }).then(res => {
      console.log(res);
      const data = res?.list?.[0];
      if (!data || !data.squareList) {
        setData([]);
        setAvgValue(0);
        return;
      }
      const chartData = data.squareList.map(item => ({
        time: item.time ?? '',
        // 要求接口没数据时与value为0时分开处理
        // value: item.value ?? 0,
        value: item.value,
      }));
      const avg = data.squareListAvg ?? 0;
      setData(chartData);
      setAvgValue(avg);
    });
  }, [rangeDate]);

  useEffect(() => {
    if (!chartRef.current) return;

    const myChart = echarts.init(chartRef.current);

    const option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        valueFormatter: (v: number) => {
          return (isNil(v) ? '--' : v) + ' m³';
        },
      },
      grid: {
        top: 95,
        bottom: 80,
      },
      xAxis: {
        type: 'category',
        data: data.map(item => item.time),
        axisLabel: {
          color: '#dfd5d5ff',
          formatter: (value: string) => value,
        },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        name: 'm³',
        nameTextStyle: {
          color: '#dfd5d5ff',
          align: 'right',
        },
        axisLabel: {
          color: '#dfd5d5ff',
          formatter: (value: string) => value,
        },
        nameGap: 30,
        splitLine: {
          lineStyle: {
            color: '#807f7dff', // 设置网格线为黄色
          },
        },
      },
      legend: {
        show: true,
        top: 10,
        right: 10,
        data: [
          {
            name: seriesName,
            icon: 'rect',
          },
        ],
        textStyle: {
          color: '#dfd5d5ff',
          fontSize: 14,
        },
      },
      series: [
        {
          type: 'bar',
          name: seriesName,
          barWidth: '30%',
          data: data.map(item => item.value?.toFixed(2)),
          itemStyle: {
            color: '#0084ff',
          },
          markLine: {
            silent: true,

            symbol: ['none', 'none'],
            label: {
              formatter: ({ value }: any) => `平均值: ${value} m³`,
              position: 'insideEndTop',
              color: '#FACC58',
            },
            lineStyle: {
              color: '#FACC58',
              type: 'dashed',
            },
            data: [
              {
                yAxis: avgValue.toFixed(2),
              },
            ],
          },
        },
      ],
    };

    myChart.setOption(option);
    return () => {
      myChart.dispose();
    };
  }, [data, avgValue]);
  return (
    <Modal
      open={showModal}
      size="big"
      maskClosable={false}
      onCancel={closeModal}
      destroyOnClose
      footer={null}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginRight: '24px' }}>
          <span>{stationType === 1 ? '自来水站详情' : stationType === 2 ? '中水站详情' : '污水站详情'}</span>
          <span
            style={{ color: '#4a90e2', cursor: 'pointer', fontSize: 14 }}
            onClick={e => {
              e.stopPropagation();
              window.open(`${window.ENERGY}/layout/home?&unitId=${unitId}`, '_blank');
            }}
          >
            更多&nbsp;{'>>'}
          </span>
        </div>
      }
    >
      <div className={styles.content}>
        <div>
          <DMYDateSwitch onRangeDateChange={setRangeDate} onActiveKeyChange={setActiveKey} />
        </div>
        <div ref={chartRef} className={styles.chart}></div>
      </div>
    </Modal>
  );
};
export default WaterWorksModal;
